import api from "../api";
import { filterLoadableItems, markImageFailed } from "./imageProbe";

/** Candidate pool from API; only browser-loadable images are kept. */
const POOL_SIZE = 48;
const VALIDATED_TARGET = 24;

let rawPool = null;
let rawInflight = null;
let validatedPool = null;
let validatedInflight = null;

function normalize(items) {
  const unique = [];
  const seen = new Set();
  for (const item of items || []) {
    if (!item?.id || seen.has(item.id)) continue;
    const images =
      Array.isArray(item.images) && item.images.length
        ? item.images
        : item.image
          ? [item.image]
          : [];
    if (!images.length || seen.has(images[0])) continue;
    seen.add(item.id);
    seen.add(images[0]);
    unique.push({ ...item, images });
  }
  return unique;
}

function ensureRawPool() {
  if (rawPool) return Promise.resolve(rawPool);
  if (!rawInflight) {
    rawInflight = api
      .getPlatformShowcase(POOL_SIZE, { random: true })
      .then((res) => {
        rawPool = normalize(res?.items);
        return rawPool;
      })
      .finally(() => {
        rawInflight = null;
      });
  }
  return rawInflight;
}

async function ensureValidatedPool() {
  if (validatedPool?.length) return validatedPool;
  if (validatedInflight) return validatedInflight;

  validatedInflight = (async () => {
    const raw = await ensureRawPool();
    validatedPool = await filterLoadableItems(raw, VALIDATED_TARGET);
    return validatedPool;
  })().finally(() => {
    validatedInflight = null;
  });

  return validatedInflight;
}

/** Drop a broken product so it isn't reused this session. */
export function discardShowcaseItem(itemOrId) {
  const id = typeof itemOrId === "string" ? itemOrId : itemOrId?.id;
  if (!id) return;
  const images =
    typeof itemOrId === "object"
      ? itemOrId.images || (itemOrId.image ? [itemOrId.image] : [])
      : [];
  for (const url of images) markImageFailed(url);
  if (rawPool) rawPool = rawPool.filter((p) => p.id !== id);
  if (validatedPool) validatedPool = validatedPool.filter((p) => p.id !== id);
}

/** First N loadable items for the hero tiles. */
export async function getHeroShowcase(count = 8) {
  const items = await ensureValidatedPool();
  return items.slice(0, count);
}

/** Loadable feed items (prefer the slice after the hero set). */
export async function getFeedShowcase(count = 12) {
  const items = await ensureValidatedPool();
  if (items.length > 8) return items.slice(8, 8 + count);
  return items.slice(0, count);
}

/** Fresh sample; still filters out URLs that fail to load. */
export async function refreshFeedShowcase(count = 12) {
  const res = await api.getPlatformShowcase(Math.max(count * 2, 24), {
    random: true,
  });
  const candidates = normalize(res?.items);
  return filterLoadableItems(candidates, count);
}
